import { ChevronLeftIcon, ChevronRightIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import React, { useEffect, useState } from 'react'
import { TripCalendarModel } from '../core/calendar'
import {
	BlankCell,
	CalendarBlock,
	CalendarHeader,
	DayCell,
	EventMark,
	NavButton,
	OutsideCell,
	PageTitle,
	WeekdayLetter,
	WeekdayRow,
	WeekRowGrid,
	Weeks,
} from './TripCalendar.styles'
import { TestIds } from '@/testIds'

export interface TripCalendarProps {
	model: TripCalendarModel
	onSelectDay: (dayIndex: number) => void
}

const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

/**
 * The trip's calendar, a month at a time: the month between its arrows, the
 * weekdays, the month's weeks. The arrows turn through the months the trip has
 * days in and nothing scrolls. An undated trip pages through day-number chips
 * the same way, under no weekday header.
 */
const TripCalendar: React.FC<TripCalendarProps> = ({ model, onSelectDay }) => {
	const { dated, pages, selectedDay, selectedPage } = model
	const [page, setPage] = useState(selectedPage)
	// A picked day always wins over wherever the reader had turned to, even
	// another day of the month they left.
	useEffect(() => setPage(selectedPage), [selectedDay, selectedPage])

	const pageIndex = Math.min(page, pages.length - 1)
	const current = pages[pageIndex]
	if (!current) return null
	const unit = dated ? 'month' : 'days'

	return (
		<CalendarBlock role="group" aria-label={dated ? 'Trip calendar' : 'Trip days'}>
			<CalendarHeader>
				{pages.length > 1 && (
					<NavButton
						type="button"
						$side="previous"
						aria-label={`Previous ${unit}`}
						disabled={pageIndex === 0}
						onClick={() => setPage(pageIndex - 1)}
					>
						<ChevronLeftIcon sx={{ fontSize: iconSizes.lg }} />
					</NavButton>
				)}
				<PageTitle aria-live="polite">{current.title}</PageTitle>
				{pages.length > 1 && (
					<NavButton
						type="button"
						$side="next"
						aria-label={`Next ${unit}`}
						disabled={pageIndex === pages.length - 1}
						onClick={() => setPage(pageIndex + 1)}
					>
						<ChevronRightIcon sx={{ fontSize: iconSizes.lg }} />
					</NavButton>
				)}
			</CalendarHeader>

			{dated && (
				<WeekdayRow aria-hidden="true">
					{WEEKDAY_LETTERS.map((letter, index) => (
						<WeekdayLetter key={`${letter}-${index}`}>{letter}</WeekdayLetter>
					))}
				</WeekdayRow>
			)}

			<Weeks>
				{current.weeks.map((week, weekIndex) => (
					<WeekRowGrid key={`${current.key}-${weekIndex}`}>
						{week.map(cell => {
							if (cell.kind === 'blank') return <BlankCell key={cell.key} aria-hidden="true" />
							if (cell.kind === 'outside') {
								return (
									<OutsideCell key={cell.key} aria-hidden="true">
										{cell.label}
									</OutsideCell>
								)
							}
							return (
								<DayCell
									key={cell.key}
									type="button"
									aria-label={cell.name}
									aria-pressed={cell.selected}
									data-testid={TestIds.itinerary.calendarDay(cell.dayIndex)}
									$selected={cell.selected}
									$colorIndex={cell.colorIndex}
									onClick={() => onSelectDay(cell.dayIndex)}
								>
									<span>{cell.label}</span>
									<EventMark
										aria-hidden="true"
										$visible={cell.hasEvents}
										$selected={cell.selected}
										$colorIndex={cell.colorIndex}
									/>
								</DayCell>
							)
						})}
					</WeekRowGrid>
				))}
			</Weeks>
		</CalendarBlock>
	)
}

export default TripCalendar
